'use client'

import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  HStack,
  Skeleton,
  Text,
} from '@chakra-ui/react'
import { ChevronLeft } from 'lucide-react'
import { useGetLabOrderByIdQuery } from '@/entities/lab/api'
import {
  EResultFlag,
  RESULT_FLAG_COLOR,
  RESULT_INTERPRETATION_COLOR,
} from '@/entities/lab/model'
import type { ILabOrderDetailAnalysis } from '@/entities/lab/model'
import { AppCard } from '@/shared/ui/card'
import {
  formatLabNorm,
  formatLabResultValue,
  getLabResearchBadgeColors,
  hasNorm,
  isTextParameter,
} from '../model/lab-research'

interface Props {
  visitId: string
  researchId: string
  basePath?: string
}

function AnalysisResultCard({
  analysis,
}: {
  analysis: ILabOrderDetailAnalysis
}) {
  const { t } = useTranslation()
  const palette = getLabResearchBadgeColors(analysis.status)

  // A written finding has no unit, and an analysis interpreted by hand (or one
  // whose parameters simply carry no ranges) has nothing to compare against —
  // in both cases the columns would be dashes all the way down, and the
  // interpretation badge below the row is the whole verdict.
  const showUnit = analysis.parameters.some(
    (p) => !isTextParameter(p) && !!p.unit,
  )
  const showNorm = analysis.parameters.some(hasNorm)
  const columns = [
    '2fr',
    '1.2fr',
    showUnit ? '1fr' : null,
    showNorm ? '1fr' : null,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <AppCard.Root>
      <Box p={6}>
        <Flex align="center" gap={3} mb={5} wrap="wrap">
          <Text fontSize="lg" fontWeight="bold">
            {analysis.analysis_name}
          </Text>
          <Badge
            px={2}
            py={0.5}
            borderRadius="md"
            fontSize="11px"
            fontWeight="medium"
            bg={palette.bg}
            color={palette.color}
          >
            {analysis.status_label || analysis.status}
          </Badge>
        </Flex>

        {/* Column headers */}
        <Grid
          templateColumns={columns}
          gap={4}
          px={2}
          pb={2}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <Text fontSize="xs" color="fg.muted">
            {t('lab.orders.detail.table.parameter')}
          </Text>
          <Text fontSize="xs" color="fg.muted" textAlign="center">
            {t('lab.orders.detail.table.result')}
          </Text>
          {showUnit && (
            <Text fontSize="xs" color="fg.muted" textAlign="center">
              {t('lab.orders.detail.table.unit')}
            </Text>
          )}
          {showNorm && (
            <Text fontSize="xs" color="fg.muted" textAlign="right">
              {/* Serology is judged against a cutoff, not a range — the column
                  is a "норма" for those analyses. */}
              {analysis.parameters.some((p) => p.cutoff_value != null)
                ? t('lab.orders.detail.table.cutoff')
                : t('lab.orders.detail.table.reference')}
            </Text>
          )}
        </Grid>

        {analysis.parameters.map((parameter) => {
          // NO_REFERENCE only means something for a parameter that is supposed
          // to be measured against a norm and was not judged at all. Once the
          // result carries an interpretation — or the parameter is text /
          // hand-interpreted and has no norm by design — the badge is noise.
          const flag =
            parameter.flag === EResultFlag.NO_REFERENCE &&
            (!hasNorm(parameter) || !!parameter.interpretation)
              ? null
              : parameter.flag

          return (
            <Box
              key={parameter.parameter_code}
              borderBottomWidth="1px"
              borderColor="border"
              _last={{ borderBottom: 'none' }}
              py={3}
              px={2}
            >
              <Grid templateColumns={columns} gap={4} alignItems="start">
                <Text fontSize="sm" fontWeight="medium">
                  {parameter.name}
                </Text>
                <Text
                  fontSize="sm"
                  textAlign={isTextParameter(parameter) ? 'left' : 'center'}
                  whiteSpace="pre-wrap"
                >
                  {formatLabResultValue(parameter)}
                </Text>
                {showUnit && (
                  <Text fontSize="sm" color="fg.muted" textAlign="center">
                    {isTextParameter(parameter) ? '—' : parameter.unit || '—'}
                  </Text>
                )}
                {showNorm && (
                  <Text fontSize="sm" color="fg.muted" textAlign="right">
                    {isTextParameter(parameter)
                      ? '—'
                      : formatLabNorm(parameter)}
                  </Text>
                )}
              </Grid>

              {/* Flag and interpretation are both computed backend-side on
                validation. */}
              {(flag ?? parameter.interpretation) && (
                <HStack gap={2} mt={2} flexWrap="wrap">
                  {flag && (
                    <Badge
                      size="sm"
                      borderRadius="md"
                      colorPalette={RESULT_FLAG_COLOR[flag] ?? 'gray'}
                    >
                      {t(`lab.result_flag.${flag}`, {
                        defaultValue: flag,
                      })}
                    </Badge>
                  )}
                  {parameter.interpretation && (
                    <Badge
                      size="sm"
                      borderRadius="md"
                      colorPalette={
                        RESULT_INTERPRETATION_COLOR[parameter.interpretation] ??
                        'gray'
                      }
                    >
                      {t(
                        `lab.interpretation_value.${parameter.interpretation}`,
                        {
                          defaultValue: parameter.interpretation,
                        },
                      )}
                    </Badge>
                  )}
                </HStack>
              )}
            </Box>
          )
        })}
      </Box>
    </AppCard.Root>
  )
}

/**
 * Read-only view of a single lab order opened from the donor card. The list
 * rows only carry a per-analysis result summary, so the full parameter table is
 * read from the lab order detail endpoint.
 */
export function LabResearchDetailTab({
  visitId,
  researchId,
  basePath = '/donors',
}: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data, isLoading, isError } = useGetLabOrderByIdQuery(researchId, {
    enabled: !!researchId,
  })
  const order = data?.data

  const backToList = () =>
    navigate({ to: `${basePath}/${visitId}/lab-research` as never })

  const backButton = (
    <Button
      variant="ghost"
      size="sm"
      borderRadius="md"
      gap={1}
      px={2}
      color="fg.muted"
      onClick={backToList}
      _hover={{ color: 'fg', bg: 'bg.muted' }}
    >
      <ChevronLeft size={16} />
      {t('donor_detail.lab_research.back')}
    </Button>
  )

  if (isLoading) {
    return (
      <Flex direction="column" gap={5}>
        {backButton}
        <Skeleton borderRadius="xl" h="260px" />
      </Flex>
    )
  }

  if (isError || !order) {
    return (
      <AppCard.Root>
        <Flex direction="column" align="center" gap={4} p={10}>
          <Text color="fg.muted" textAlign="center">
            {t('donor_detail.lab_research.not_found')}
          </Text>
          <Button variant="outline" borderRadius="md" onClick={backToList}>
            {t('donor_detail.lab_research.back')}
          </Button>
        </Flex>
      </AppCard.Root>
    )
  }

  return (
    <Flex direction="column" gap={5}>
      <Flex align="center" gap={3} wrap="wrap">
        {backButton}
        <Heading size="lg">
          {t('lab.orders.detail.title', { id: order.order_number })}
        </Heading>
      </Flex>

      {order.analyses.length === 0 ? (
        <AppCard.Root>
          <Flex justify="center" p={10}>
            <Text color="fg.muted">{t('donor_detail.lab_research.empty')}</Text>
          </Flex>
        </AppCard.Root>
      ) : (
        order.analyses.map((analysis) => (
          <AnalysisResultCard key={analysis.id} analysis={analysis} />
        ))
      )}
    </Flex>
  )
}
